import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaRocket,
  FaShieldAlt,
  FaArrowLeft,
  FaStar,
  FaUserCheck,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useDispatch } from "react-redux";
import { setUser } from "../store/slices/authSlice";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const VerifyEmailSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setShowFooter } = useFooter();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const userParam = params.get("user");

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        localStorage.setItem("token", token);
        dispatch(setUser(user));
        toast.success("Email verified! Welcome to VTUGenX!");

        setTimeout(() => {
          navigate("/dashboard");
        }, 3000);
      } catch (error) {
        console.error("Error parsing user data:", error);
        toast.error(
          "Verification successful but failed to login. Please login manually.",
        );
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } else {
      // If no token, redirect to login
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  }, [location, navigate, dispatch]);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.9)" : "rgba(255, 255, 255, 0.95)";

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "500px",
          width: "100%",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: isMobile ? "2rem" : "2.5rem",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${getBorderColor()}`,
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        {/* Back Button */}
        <motion.div
          variants={itemVariants}
          style={{ position: "absolute", top: "1.5rem", left: "1.5rem" }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              border: "none",
              padding: "0.5rem",
              borderRadius: "10px",
              cursor: "pointer",
              color: getTextSecondary(),
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              fontSize: "0.8rem",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)")
            }
          >
            <FaArrowLeft size={12} /> Back
          </button>
        </motion.div>

        {/* Success Icon */}
        <motion.div
          variants={iconVariants}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #10b981, #059669)",
              width: "90px",
              height: "90px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              boxShadow: "0 20px 40px -12px rgba(16,185,129,0.4)",
            }}
          >
            <FaCheckCircle size={50} color="white" />
          </div>
        </motion.div>

        {/* Success Title */}
        <motion.h2
          variants={itemVariants}
          style={{
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, #10b981, #059669)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700,
            fontSize: isMobile ? "1.8rem" : "2rem",
          }}
        >
          Email Verified!
        </motion.h2>

        {/* Message */}
        <motion.p
          variants={itemVariants}
          style={{ color: getTextSecondary(), marginBottom: "1rem" }}
        >
          Your email has been successfully verified.
        </motion.p>

        {/* Welcome Message */}
        <motion.div
          variants={itemVariants}
          style={{
            background: isDarkMode
              ? "rgba(16,185,129,0.1)"
              : "rgba(16,185,129,0.05)",
            padding: "1rem",
            borderRadius: "16px",
            marginBottom: "1.5rem",
            border: `1px solid rgba(16,185,129,0.2)`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <FaUserCheck size={16} color="#10b981" />
            <span
              style={{
                fontSize: "0.85rem",
                color: "#10b981",
                fontWeight: "500",
              }}
            >
              Welcome to the VTUGenX community!
            </span>
          </div>
        </motion.div>

        {/* Redirecting Message */}
        <motion.p
          variants={itemVariants}
          style={{
            color: getTextSecondary(),
            fontSize: "0.85rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
          >
            <FaRocket size={14} color="#8b5cf6" />
          </motion.div>
          Redirecting to dashboard...
        </motion.p>

        {/* Progress Bar */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "1.5rem",
            height: "4px",
            background: isDarkMode ? "rgba(139,92,246,0.1)" : "#e2e8f0",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 3, ease: "linear" }}
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4)",
              borderRadius: "2px",
            }}
          />
        </motion.div>

        {/* Security Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${getBorderColor()}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            Your account is now verified and secured
          </span>
          <FaStar size={10} color="#f59e0b" />
        </motion.div>
      </motion.div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default VerifyEmailSuccess;
